/* eslint-disable array-callback-return */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import FullCalendar from "@fullcalendar/react"; // must go before plugins
import interactionPlugin from "@fullcalendar/interaction"; // needed for dayClick
import moment from "moment";
import resourceTimelinePlugin from "@fullcalendar/resource-timeline"; // a plugin!
import SnackBarTool from "../../../../_metronic/_partials/widgets/snack-bar/SnackBar";
import { CustomeProgressBar } from "../../../../_metronic/_partials/controls/CustomProgressBar";
import { breakString, getApiErrorMessage } from "../../../utils/commonFunction";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { paymentDue, timeSlotUpdate, unblockSlot } from "../redux/calendarCrud";
import { Modal } from "react-bootstrap";
import { useIntl } from "react-intl";
import { capitalizeFirstLetter } from "../../../utils/canvaCloseFunction";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import { LoadingSpinner } from "../../../../_metronic/_partials/controls/LoadingSpinner";
import dayGridPlugin from "@fullcalendar/daygrid";
import { getContainerResourceLabel } from "./calendarHelpers";

/** Synthetic resource id for the row that surfaces orders with no van assigned. */
const UNALLOCATED_RESOURCE_ID = "unallocated";

/**
 * Make incomplete booking jobs visible and allocatable on the van calendar.
 *
 * Two gaps hide a job. An order with no assigned van arrives with an empty
 * resourceId, so it belongs to no van row: those are collected onto a synthetic
 * "Unallocated" row and greyed, since they need action rather than reading as
 * healthy booked jobs. An order with no time slot arrives with a start of the
 * form "2026-07-18T+00:00", which FullCalendar cannot parse, so the job silently
 * disappears: those are repaired into an all-day bar on the date and titled
 * "No time set". A job can have either gap or both — the time repair applies to
 * allocated jobs too, otherwise a job allocated a van while still awaiting its
 * slot would vanish from the calendar.
 *
 * Both kinds can be dragged onto a van row to allocate them. A job with no slot
 * is flagged `hasNoTimeSlot` so the drop posts the van on its own and leaves the
 * slot unbooked — reading a time off its all-day bar would book 00:00.
 *
 * @param {object} vans - The `vans` payload from the booking calendar API
 *   (`{ resources, events }`).
 * @returns {object} The same payload with incomplete jobs repaired, and an
 *   "Unallocated" resource prepended when any job has no van.
 */
const normalizeUnallocatedVans = (vans) => {
  const events = vans?.events || [];
  const resources = vans?.resources || [];
  let hasUnallocated = false;
  const fixedEvents = events.map((event) => {
    // Only booking jobs (blockings carry blockId, guide slots have no hoverDetails)
    if (!event?.hoverDetails?.order_id) return event;
    const isUnallocated = !event?.resourceId;
    const hasNoTimeSlot =
      typeof event.start === "string" &&
      /^\d{4}-\d{2}-\d{2}T(?!\d)/.test(event.start);
    if (!isUnallocated && !hasNoTimeSlot) return event;
    const fixed = { ...event };
    if (isUnallocated) {
      hasUnallocated = true;
      fixed.resourceId = UNALLOCATED_RESOURCE_ID;
      // Grey instead of the collection/return colour. That colour is what
      // identifies the job as a Collection or a Return, so carry it on the
      // event for the drop handler to read back.
      fixed.color = "#808080";
      fixed.originalColor = event.color || event.backgroundColor || "";
    }
    if (hasNoTimeSlot) {
      // Render as an all-day bar on the date, and flag it so a drop allocates
      // the van without inventing a time slot from the bar.
      fixed.start = event.start.split("T")[0];
      delete fixed.end;
      fixed.allDay = true;
      fixed.hasNoTimeSlot = true;
      const existingTitle = String(fixed.title || "")
        .replace(/^-:\s*/, "")
        .trim();
      const title = existingTitle
        ? `No time set: ${existingTitle}`
        : "No time set";
      fixed.title = title;
      fixed.hoverDetails = { ...fixed.hoverDetails, title };
    }
    return fixed;
  });
  if (!hasUnallocated) return { ...vans, events: fixedEvents };
  return {
    ...vans,
    resources: [
      {
        id: UNALLOCATED_RESOURCE_ID,
        title: "Unallocated",
        allotted_pods: null,
        pods_available: 0,
      },
      ...resources,
    ],
    events: fixedEvents,
  };
};

/**
 * Colour the API gives a pod partial return. It is deliberately not the return
 * colour: a job's type is read off its colour when it is dragged, and a
 * partial return sent as "Return" would overwrite the order's own return slot.
 */
const PARTIAL_RETURN_COLOR = "#c2410c";

/**
 * Whether a colour identifies a van job (collection, return or partial
 * return), as opposed to a blocking or a guide slot.
 *
 * @param {string} color - The job's identifying colour.
 * @returns {boolean}
 */
const isJobColor = (color) =>
  color === "#ff6601" || color === "#009933" || color === PARTIAL_RETURN_COLOR;

/**
 * Resolve the colour that identifies a job as a Collection or a Return.
 *
 * The drop/resize flow reads the job type, the confirmation wording and the
 * update payload's `order_type` off the event colour. Unallocated jobs are
 * re-coloured grey for display, so the rendered colour no longer identifies
 * them — fall back to the colour preserved by `normalizeUnallocatedVans`.
 *
 * @param {object} eventApi - A FullCalendar event API object.
 * @returns {string} The job's identifying colour, or "" when unknown.
 */
const resolveJobColor = (eventApi) =>
  eventApi?.extendedProps?.originalColor || eventApi?.backgroundColor || "";

/**
 * FullCalendar resource-timeline for van-based scheduling (Pod / collection /
 * return views), including the synthetic "Unallocated" van row that surfaces
 * jobs with no assigned van. Renders resource rows with availability labels,
 * draggable/resizable event slots with hover tooltips, and a confirmation
 * modal for slot moves, resizes and unblocking.
 *
 * @param {object} props - Component props.
 * @param {Date|number|string} props.collectionDate - Date the calendar navigates to.
 * @param {object} props.calendarData - API payload with `vans`/`site_class` resources and events.
 * @param {object} props.formik - Formik bag for the collection/return form.
 * @param {string} props.keys - Booking side being edited: "collection" or "return".
 * @param {object} props.activeEvent - Currently selected FullCalendar event instance.
 * @param {Function} props.setActiveEvent - Setter for the active event.
 * @param {string} props.type - "calendar" for the admin calendar view, otherwise booking/edit mode.
 * @param {Function} props.setActionButton - Setter used to trigger refetch/action state.
 * @param {object} props.actionButton - Action-button state (e.g. `type`, `check`).
 * @param {boolean} props.isOverWrite - Whether overwriting a full slot requires confirmation.
 * @param {Function} props.setFormData - Setter for the calendar filter form data.
 * @param {object} props.formData - Calendar filter form data (e.g. selected `date`).
 * @param {string} props.keyCon - "container" for the container view, "" for van/pod views.
 * @param {Function} props.setContainerData - Setter for the container week-range data.
 * @returns {JSX.Element} The configured FullCalendar with its confirmation modal.
 */
export const CalendarComponent = ({
  collectionDate,
  calendarData,
  formik,
  keys,
  activeEvent,
  setActiveEvent,
  type,
  setActionButton,
  actionButton,
  isOverWrite,
  setFormData,
  formData,
  keyCon,
  setContainerData,
}) => {
  const calendarRef = useRef();
  const abortControllerRef = useRef(null);

  // const navigate = useNavigate();
  const [snack, setSnack] = useState({ call: false, type: "", message: "" });
  const [dialog, setDialog] = useState({ type: false, event: "", check: "" });
  const [invoice, setInvoice] = useState({ loading: false, is_invoice: 0 });

  // Container view renders site_class events against untouched van resources;
  // only normalise the vans payload for the pod calendars.
  const normalizedVans = useMemo(
    () =>
      keyCon === "container"
        ? calendarData?.data?.vans
        : normalizeUnallocatedVans(calendarData?.data?.vans),
    [calendarData, keyCon]
  );

  const getCalendarApi = () => {
    const { current } = calendarRef;
    return current?.getApi();
  };
  const handleDate = useCallback(() => {
    const calendarApi = getCalendarApi();
    if (calendarApi) {
      calendarApi.gotoDate(collectionDate);
    }
  }, [collectionDate]);
  useEffect(() => {
    if (collectionDate) {
      handleDate();
    }
  }, [collectionDate, handleDate]);
  const handleEventClick = (e) => {
    if (type === "calendar") {
      // if (e?.event?.extendedProps?.hoverDetails?.order_id) {
      //   navigate(
      //     `/order-details/${e.event.extendedProps.hoverDetails.order_id}`
      //   );
      // }
      if (e?.event?.extendedProps?.hoverDetails?.order_id) {
        const url = `/order-details/${e.event.extendedProps.hoverDetails.order_id}`;
        window.open(url, "_blank");
      }
      return;
    }
    setSnack({
      call: false,
      type: "",
      message: "",
    });
    let eventObj = e.event;

    if (activeEvent) {
      activeEvent.setProp("classNames", []);
    }
    eventObj.setProp("classNames", ["clicked-event"]);
    setActiveEvent(eventObj);
    if (eventObj?.backgroundColor === "white") {
      keys === "collection"
        ? formik.setValues({
            ...formik.values,
            collection_date: moment(eventObj?.startStr).format("YYYY-MM-DD"),
            start_time_slot: moment.utc(eventObj?.startStr).format("HH:mm"),
            end_time_slot: moment.utc(eventObj?.endStr).format("HH:mm"),
            collection_van_id: eventObj?._def?.resourceIds[0] || "",
            van_id: eventObj?._def?.resourceIds[0] || "",
            is_student: "0",
            is_return: "0",
            max_pod: eventObj?.extendedProps?.totalPodsAvailable || "",
          })
        : formik.setValues({
            ...formik.values,
            return_date: moment(eventObj?.startStr).format("YYYY-MM-DD"),
            return_start_time_slot: moment
              .utc(eventObj?.startStr)
              .format("HH:mm"),
            return_end_time_slot: moment.utc(eventObj?.endStr).format("HH:mm"),
            return_van_id: eventObj?._def?.resourceIds[0] || "",
            van_id: eventObj?._def?.resourceIds[0] || "",
            is_student_return: "0",
            is_return: formik?.values?.is_return === "0" ? "0" : "1",

            // is_return: formik?.values?.is_return === "0" ? "0" : "1",
          });
      if (isOverWrite) {
        setDialog({ type: false, event: "", check: "" });
      }
    } else {
      // setSnack({ call: true, type: "error", message: "Can't add this" });
      if (isOverWrite) {
        setDialog({ type: false, event: "", check: "" });
      }
    }
  };

  /**
   * Render the resource-column label: plain site name for the Container view,
   * a plain label for the synthetic Unallocated row, or a pod-availability
   * heading with a progress bar otherwise.
   *
   * @param {object} e - The FullCalendar `resourceLabelContent` callback argument.
   * @returns {string|JSX.Element} The label text (container) or availability markup.
   */
  const handleResourceLabelContent = (e) => {
    if (keyCon === "container") return getContainerResourceLabel(e);
    // The synthetic Unallocated row has no availability figures — plain label only
    if (e?.resource?.id === UNALLOCATED_RESOURCE_ID) {
      return (
        <div>
          <h4>{e?.fieldValue}</h4>
        </div>
      );
    }
    const podCount = e?.fieldValue.charAt(e?.fieldValue?.length - 3);
    const progress = Number(podCount) * 16.67;
    const string = breakString(e?.fieldValue, "Availability");
    return (
      <div>
        {string?.length > -1 &&
          string?.map((data, index) => (
            <div key={index}>
              {index === 0 ? (
                <h4 key={index}>{data}</h4>
              ) : (
                <span key={index}>{data}</span>
              )}
            </div>
          ))}
        <div className="mt-2" style={{ width: "80%" }}>
          <CustomeProgressBar variant={"warning"} now={progress} />
        </div>
      </div>
    );
  };

  const intl = useIntl();
  /**
   * Persist a job or blocking that the user has moved, resized or dragged onto
   * another van, then close the confirmation modal and trigger a refetch.
   *
   * @param {object} event - The FullCalendar eventResize/eventDrop callback argument.
   * @param {string} [check] - "drop" when the event changed row, so the row it
   *   landed on is posted as the van. A drop from the Unallocated row posts that
   *   van alone, leaving the order's time slot as it is.
   * @returns {void}
   */
  const handleResize = (event, check) => {
    const value = event?.event;
    // A partial return is identified by its own id, so the change is written
    // to the partial return and never to the order's full return.
    const partialReturnId =
      value?.extendedProps?.hoverDetails?.partial_return_id || "";
    const data = {
      order_id: value?.extendedProps?.hoverDetails?.order_id || "",
      order_type: partialReturnId
        ? "Partial Return"
        : dialog.color === "#009933"
          ? "Collection"
          : dialog.color === "#ff6601"
          ? "Return"
          : dialog.block === "Block"
          ? "Block"
          : dialog.block === "Travel"
          ? "Travel"
          : "",
    };
    if (partialReturnId) {
      data.partial_return_id = partialReturnId;
    }
    // Allocating a van records who does the job; it does not reschedule it, so
    // no times are posted and the order keeps whatever slot it has. Reading them
    // off the bar would write nonsense: a job with no slot renders all-day, so
    // its start is midnight, and one with a start but no end is given an end an
    // hour from the current time by the calendar API.
    const isAllocation = event?.oldResource?.id === UNALLOCATED_RESOURCE_ID;
    if (!isAllocation && !value?.extendedProps?.hasNoTimeSlot) {
      data.time_from = moment.utc(value?.startStr).format("HH:mm") || "";
      data.time_to = moment.utc(value?.endStr).format("HH:mm") || "";
    }
    if (dialog.block === "Travel" || dialog?.block === "Block") {
      data.block_id = value?.extendedProps?.hoverDetails?.blockId || "";
    }
    if (check === "drop") {
      data.van_id = event?.newResource?.id || "";
      // An unallocated job has to land on a van row. Without a van_id the
      // update would move the customer's time slot and leave the job
      // unallocated, so revert the drag instead of posting it.
      if (event?.oldResource?.id === UNALLOCATED_RESOURCE_ID && !data.van_id) {
        event?.revert?.();
        setDialog({ type: false, event: "", check: "" });
        setSnack({
          call: true,
          type: "error",
          message: "Drop this job on a van row to allocate it.",
        });
        return;
      }
    }
    timeSlotUpdate(data)
      .then(() => {
        setActionButton({ ...actionButton, check: "resize" });
        setDialog({ type: false, event: "", check: "" });
      })
      .catch((error) => {
        // Nothing was saved, so put the event back where it was rather than
        // leaving the calendar showing a move the server rejected.
        event?.revert?.();
        setDialog({ type: false, event: "", check: "" });
        setSnack({
          call: true,
          type: "error",
          message: getApiErrorMessage(error),
        });
      });
  };

  /**
   * Unblock a Block or Travel slot on the admin calendar, then refetch.
   *
   * @param {Object} eventInfo The FullCalendar event that was clicked.
   * @returns {void}
   */
  const handleDelete = (eventInfo) => {
    if (type !== "calendar") return;

    if (
      eventInfo?.event?.extendedProps?.block === "Block" ||
      eventInfo?.event?.extendedProps?.block === "Travel"
    ) {
      setSnack({
        call: false,
        type: "",
        message: "",
      });
      const data = {
        id: eventInfo?.event?.extendedProps?.hoverDetails?.blockId || "",
      };
      unblockSlot(data)
        .then((res) => {
          setActionButton({ ...actionButton, check: "resize" });
          setSnack({ call: true, type: "success", message: res.data.message });
          setDialog({ type: false, event: "", check: "" });
        })
        .catch((error) => {
          // On error, update snackbar message
          setSnack({
            call: true,
            type: "error",
            message: getApiErrorMessage(error),
          });
        });
    }
  };

  const [validRange, setValidRange] = useState({});

  useEffect(() => {
    /**
     * Allow dates from the given day onwards.
     *
     * @param {Date} currentDate The first selectable day.
     * @returns {void}
     */
    const handleDates = (currentDate) => {
      const start = currentDate.toISOString().split("T")[0];
      setValidRange({ start });
    };

    const collectionDate = formik?.values?.collection_date;
    const currentDate = new Date();

    if (type !== "calendar") {
      if (collectionDate && keys === "return") {
        const nextDate = new Date(collectionDate);
        nextDate.setDate(nextDate.getDate() ); // Get the next date after the collection date
        handleDates(nextDate);
      } else if (collectionDate) {
        const dates = new Date(collectionDate);
        if (dates < currentDate) {
          handleDates(dates);
        } else {
          handleDates(currentDate);
        }
      } else {
        handleDates(currentDate); // Call handleDates once during initial render
      }
    }

    return () => {
      // Cleanup function
      setValidRange({});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  /**
   * Prepend a star icon to a timeline event's element.
   *
   * @param {Object} event The calendar event.
   * @param {Object} element The event's jQuery-wrapped element.
   * @returns {void}
   */
  const eventRender = (event, element) => {
    // Add custom icon to the event element
    const iconClass = event.iconClass;
    // if (iconClass) {
    element
      .find("div.fc-timeline-event")
      .prepend(`<i className="fa fa-star"></i> `);
    // }
  };

  /**
   * Look up whether the hovered job's order has invoices due, for the
   * "Payment due" badge, cancelling the previous lookup still in flight.
   *
   * @param {number|string} value The hovered job's order id.
   * @returns {void}
   */
  const handlePaymentDue = (value) => {
    setInvoice({
      loading: true,
      is_invoice: 0,
    });
    if (abortControllerRef?.current) {
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    paymentDue(value, abortController).then((res) => {
      setInvoice({
        loading: false,
        is_invoice: res?.data?.data?.is_invoices_due === 1 ? 1 : 0,
      });
    });
  };
  const dummy = [
    {
      id: 37,
      title: "Class A",
      allotted_pods: null,
      pods_available: 6,
    },
    {
      id: 38,
      title: "Class B",
      allotted_pods: null,
      pods_available: 6,
    },
  ];
  const dummyEvent = [
    {
      resourceId: 37,
      title: "Guide 6 Pods 08:00-12:00",
      start: "2024-02-19T08:00:00",
      end: "2024-02-19T13:00:00",
      textColor: "black",
      color: "lightgrey",
      backgroundColor: "white",
      totalPodsAvailable: 6,
    },
    {
      resourceId: 37,
      title: "Guide 3 Pods 13:00-17:00",
      start: "2024-02-19T13:00:00+00:00",
      end: "2024-02-19T18:00:00+00:00",
      textColor: "black",
      color: "lightgrey",
      backgroundColor: "white",
      totalPodsAvailable: 3,
    },
    {
      resourceId: 38,
      title: "Guide 6 Pods 08:00-12:00",
      start: "2024-02-19T08:00:00",
      end: "2024-02-19T13:00:00",
      textColor: "black",
      color: "lightgrey",
      backgroundColor: "white",
      totalPodsAvailable: 6,
    },
    {
      resourceId: 38,
      title: "Guide 3 Pods 13:00-17:00",
      start: "2024-02-19T13:00:00+00:00",
      end: "2024-02-19T18:00:00+00:00",
      textColor: "black",
      color: "lightgrey",
      backgroundColor: "white",
      totalPodsAvailable: 3,
    },
  ];

  return (
    <>
      <FullCalendar
        className="hello "
        navLinks={false}
        ref={calendarRef}
        validRange={validRange}
        initialDate={moment(
          collectionDate ? collectionDate : new Date()
        ).format("YYYY-MM-DD")}
        eventAllow={(dropInfo, event) => {
          // Never allow dropping a job onto the synthetic Unallocated row
          if (dropInfo?.resource?.id === UNALLOCATED_RESOURCE_ID) {
            return false;
          }
          if (
            event?.backgroundColor === "white" ||
            event?.extendedProps?.hoverDetails?.is_completed === 1
          ) {
            return false;
          } else {
            return true;
          }
        }}
        editable={type === "calendar" ? true : false}
        // selectable={true}
        datesSet={(e) => {
          // if (keyCon === "container") {
          //   const startOfWeek = moment(e?.startStr)
          //     .startOf("week")
          //     .format("DD-MM-yyyy");
          //   const endOfWeek = moment(e?.startStr)
          //     .endOf("week")
          //     .format("DD-MM-yyyy");
          //   setContainerData({
          //     startOfWeek: startOfWeek,
          //     endOfWeek: endOfWeek,
          //   });
          //   return;
          // }
          if (type === "calendar") {
            return setFormData({
              ...formData,
              date: moment(e?.startStr).format("yyyy-MM-DD"),
            });
          } else if (
            (keys === "collection" && formik.values?.collection_date) ||
            (keys === "return" && formik.values?.return_date)
          ) {
            return formik?.setFieldValue(
              keys === "collection" ? "collection_date" : "return_date",
              moment(e?.startStr).format("yyyy-MM-DD")
            );
          }
        }}
        resourceAreaWidth="20%"
        contentHeight="auto"
        schedulerLicenseKey="CC-Attribution-NonCommercial-NoDerivatives"
        plugins={[resourceTimelinePlugin, interactionPlugin, dayGridPlugin]}
        defaultView="resourceTimelineDay"
        height={300}
        timeZone="UTC"
        initialView="resourceTimeline"
        slotMinTime="08:00:00"
        slotMaxTime={
          actionButton?.type === "operation" ? "20:30:00" : "18:00:00"
        }
        headerToolbar={{
          left: "",
          center: "prev,title,next",
          right: "",
        }}
        //customWeeks,customMonth,customDay
        views={{
          customWeeks: {
            type: "resourceTimeline",
            duration: { weeks: 1 },
            slotDuration: { days: 2 },
            buttonText: "Week",
          },

          customMonth: {
            type: "resourceTimeline",
            duration: { months: 1 },
            slotDuration: { days: 1 },
            buttonText: "Month",
          },
          customDay: {
            type: "resourceTimeline",
            duration: { days: 1 },
            slotDuration: { days: 1 },
            buttonText: "Day",
          },
        }}
        slotLabelInterval={
          actionButton?.type === "operation" ? "00:30:00" : "01:00:00"
        }
        // customButtons={customButtons}
        eventClick={(e) => {
          if (isOverWrite) {
            setDialog({
              type: true,
              event: e,
              check: "over",
            });
            return;
          }
          handleEventClick(e);
        }}
        eventResize={(event) => {
          setDialog({
            type: true,
            event,
            block: event?.event?.extendedProps?.block,
            color: resolveJobColor(event?.event),
          });
        }}
        eventDrop={(event) => {
          setDialog({
            type: true,
            event,
            check: "drop",
            block: event?.event?.extendedProps?.block,
            color: resolveJobColor(event?.event),
          });
          // handleResize(event, "drop");
        }}
        event
        resourceAreaHeaderContent=" "
        resourcesInitiallyExpanded={false}
        resourceOrder="from"
        resources={
          keyCon === "container"
            ? calendarData?.data?.site_class?.resources
            : normalizedVans?.resources
        }
        events={
          keyCon === "container"
            ? calendarData?.data?.site_class?.events
            : normalizedVans?.events
        }
        eventRender={eventRender}
        resourceLabelContent={handleResourceLabelContent}
        eventContent={(eventInfo) => {
          return (
            <>
              <div>
                {eventInfo.event?.extendedProps?.hoverDetails ? (
                  <div
                    style={{ backgroundColor: `${eventInfo.event.color}` }}
                    onMouseEnter={() =>
                      handlePaymentDue(
                        eventInfo.event?.extendedProps?.hoverDetails?.order_id
                      )
                    }
                  >
                    <OverlayTrigger
                      overlay={
                        <Tooltip
                          id="products-edit-tooltip"
                          className="tooltipwth"
                        >
                          <form style={{ width: "400px !important" }}>
                            <div className="row">
                              <>
                               
                                {invoice?.is_invoice > 0 ? (
                                  <span
                                    className={`badge badge-pill ml-2 mb-4 ${"badge-danger"}`}
                                  >
                                    {"Payment due"}
                                  </span>
                                ) : invoice.loading ? (
                                  <span className="ml-2">
                                    <LoadingSpinner />
                                  </span>
                                ) : (
                                  ""
                                )}
                                {Object.entries(
                                  eventInfo?.event?.extendedProps?.hoverDetails
                                ).map(([keys, value], index) => {
                                  if (
                                    keys === "order_id" ||
                                    keys === "blockId" ||
                                    keys === "partial_return_id" ||
                                    (keys === "is_invoices_due" && value < 1)
                                  ) {
                                    return;
                                  }
                                  return (
                                    <div
                                      className="col-12"
                                      key={index}
                                      style={{
                                        width: "200px",
                                        color:
                                          keys === "is_invoices_due" &&
                                          value > 0
                                            ? "red"
                                            : "",
                                      }}
                                    >
                                      <div className="mt-4 d-flex  mb-2">
                                        {keys !== "message" &&
                                          keys !== "blockId" && (
                                            <span
                                              className="font-weight-bolder "
                                              style={{ fontSize: "1em" }}
                                            >
                                              {keys === "is_invoices_due" &&
                                              value > 0 ? (
                                                <span
                                                  className={`badge badge-pill ${"badge-danger"}`}
                                                >
                                                  {"Payment due"}
                                                </span>
                                              ) : (
                                                capitalizeFirstLetter(
                                                  keys?.replace(/_/g, " ")
                                                ) + ":"
                                              )}{" "}
                                            </span>
                                          )}
                                        <span
                                          className="  ml-1"
                                          style={{ fontSize: "1em" }}
                                        >
                                          {keys === "is_invoices_due" &&
                                          value > 0
                                            ? ""
                                            : value}
                                        </span>
                                      </div>
                                    </div>
                                  );
                                })}
                              </>
                            </div>
                          </form>
                        </Tooltip>
                      }
                    >
                      <div
                        className="fc-event-main-frame svg-icon svg-icon-md svg-icon-primary"
                        onDoubleClick={() => {
                          if (type !== "calendar") return;
                          if (eventInfo?.backgroundColor === "#242939") {
                            setDialog({
                              type: true,
                              event: eventInfo,
                              block: eventInfo?.event?.extendedProps?.block,
                              // color: eventInfo?.backgroundColor,
                              check: "drop",
                              isDelete: true,
                            });
                          }
                        }}
                      >
                        <div className="fc-event-title-container">
                          <div
                            className="fc-event-title fc-sticky"
                            style={{ top: "0px" }}
                          >
                            {eventInfo.event?.extendedProps?.hoverDetails
                              ?.is_invoices_due > 0 && (
                              <i
                                className="fa fa-exclamation-circle mr-2"
                                style={{ color: "#544b4b" }}
                              ></i>
                            )}
                            {eventInfo?.event?.title}&nbsp;
                            {eventInfo?.event?.extendedProps?.block === "Block"
                              ? eventInfo?.event?.extendedProps?.hoverDetails
                                  ?.reason
                              : ""}
                          </div>
                        </div>
                      </div>
                    </OverlayTrigger>
                  </div>
                ) : (
                  <div
                    className="fc-event-main-frame svg-icon svg-icon-md svg-icon-primary"
                    onDoubleClick={() => {
                      if (type !== "calendar") return;
                      if (eventInfo.backgroundColor === "#242939") {
                        setDialog({
                          type: true,
                          event: eventInfo,
                          block: eventInfo?.event?.extendedProps?.block,
                          // color: eventInfo?.backgroundColor,
                          check: "drop",
                          isDelete: true,
                        });
                      }
                    }}
                  >
                    <div className="fc-event-title-container">
                      <div
                        className="fc-event-title fc-sticky"
                        style={{ top: "0px" }}
                      >
                        {eventInfo?.event?.title}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          );
        }}
        // viewSkeletonRender={handleViewRender}
      />
      {snack.call ? <SnackBarTool {...snack} /> : ""}
      <Modal show={dialog?.type} aria-labelledby="example-modal-sizes-title-lg">
        <Modal.Header closeButton>
          <Modal.Title id="example-modal-sizes-title-lg">
            {// isOverWrite ? (
            //   intl.formatMessage({ id: "Overwrite.Maximum.Slot" })
            // )
            // :
            dialog?.isDelete ? (
              <>
                {dialog.block === "Travel"
                  ? intl.formatMessage({ id: "Unblock.Travel.Time" })
                  : dialog?.block === "Block"
                  ? intl.formatMessage({ id: "Unblocking.Slot" })
                  : ""}
              </>
            ) : (
              <>
                {isJobColor(dialog.color)
                  ? intl.formatMessage({ id: "Change.Job.Time.Slot" })
                  : dialog.block === "Travel"
                  ? intl.formatMessage({ id: "Change.Travel.Time" })
                  : dialog?.block === "Block"
                  ? intl.formatMessage({ id: "Change.Slot.Time" })
                  : ""}
              </>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {// isOverWrite ? (
          //   intl.formatMessage({ id: "Have.you.approval.overwrite.time.slot" })
          // )
          // :
          dialog?.isDelete ? (
            <span>
              {dialog.block === "Travel"
                ? intl.formatMessage({ id: "Are.unblock.travel.time" })
                : dialog?.block === "Block"
                ? intl.formatMessage({ id: "Are.unblock.slot?" })
                : ""}
            </span>
          ) : (
            <>
              <span>
                {isJobColor(dialog.color)
                  ? intl.formatMessage({ id: "Are.Job.Time.Slot?" })
                  : dialog.block === "Travel"
                  ? intl.formatMessage({ id: "Are.travel.time" })
                  : dialog?.block === "Block"
                  ? intl.formatMessage({ id: "Are.slot.time" })
                  : ""}
              </span>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div>
            <button
              type="button"
              onClick={() => {
                // Declining a move must undo it on the spot; the refetch below
                // would otherwise be the only thing putting the event back.
                // A no-op for the click-driven overwrite and delete dialogs,
                // whose events carry no revert callback.
                dialog?.event?.revert?.();
                setDialog({ type: false, event: "", check: "" });
                if (isOverWrite) return;
                setActionButton({ ...actionButton, check: "resize" });
              }}
              className="btn btn-light btn-elevate"
            >
              {intl.formatMessage({ id: "No" })}
            </button>
            <> </>
            <button
              type="button"
              onClick={() => {
                isOverWrite
                  ? handleEventClick(dialog?.event)
                  : dialog?.isDelete
                  ? handleDelete(dialog?.event)
                  : handleResize(dialog?.event, dialog?.check);
              }}
              className="btn btn-delete btn-elevate"
            >
              {intl.formatMessage({ id: "Yes" })}
            </button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

// {
//   color: "grey",
//   end: "2023-02-03T17:05:00+00:00",
//   resourceId: 27,
//   start: "2023-02-03T08:00:00+00:00",
//   title: "08:00-16:05",
//   display: "block",
//   eventOverlap: false,
//   rendering: "background",
// },
